// src/components/penalizacao/PenalizacaoPanel.jsx
import React, { useMemo, useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getPenalizacoesRelatorio } from '../../api/penalizacoes';
import { Typography, Box, TextField, Stack, Tooltip, Chip, IconButton, Paper, Divider } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { DataGrid } from '@mui/x-data-grid';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import FilterAltRoundedIcon from '@mui/icons-material/FilterAltRounded';
import dayjs from 'dayjs';
import IconNaoRealizada from '../../assets/naoRealizada.png'; // "Viagens penalizadas"
import MultaIcon from '../../assets/multa.png';                 // "Penalizações (R$)"
import RankingPenalizacoesPorEmpresa from './RankingPenalizacoesPorEmpresa.jsx';
const EMPRESAS_FIXAS = [2496, 2497, 2498, 2499, 2500, 2501, 2502];


// ---------------- helpers ----------------
function parseBRNumber(v) {
  if (typeof v === 'number') return v;
  if (typeof v === 'string') {
    const n = Number(v.replace(/\./g, '').replace(',', '.'));
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}
function penalFmt(v) {
  const n = parseBRNumber(v);
  return n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// --- helpers novos/atualizados ---
const isHex24 = (s) => /^[a-f0-9]{24}$/i.test((s || '').toString().trim());
const isSentidoToken = (s) => /^(IDA|VOLTA|ID|VO)$/i.test((s || '').toString().trim());

// NOVO: captura sublinha completa "A307.1", "011.4", "446.1"
// aceita separador "." ou "_", mas normaliza para "."
function subToken(input) {
  const t = String(input || '').toUpperCase();
  const m = t.match(/\b([A-Z]?\d{2,4})[._](\d)\b/);
  if (!m) return '';
  const base = m[1];
  if (base.length === 4 && base.startsWith('20')) return ''; // evita "2025.1"
  return `${base}.${m[2]}`;
}

// NOVO: procura o primeiro match de regex dentro de QUALQUER string do objeto
function deepFindFirstMatch(obj, rx) {
  const seen = new Set();
  const stack = [obj];
  while (stack.length) {
    const cur = stack.pop();
    if (!cur || typeof cur !== 'object' || seen.has(cur)) continue;
    seen.add(cur);
    for (const v of Object.values(cur)) {
      if (typeof v === 'string') {
        const up = v.toUpperCase();
        const m = rx.exec(up);
        if (m) return m;
      } else if (v && typeof v === 'object') {
        stack.push(v);
      }
    }
  }
  return null;
}

// ATUALIZE: agora respeita sublinha e casos com "PRG_..."
function pickLinhaNum(v) {
  if (v == null) return '';

  if (typeof v === 'number') {
    const s = String(v);
    return /^\d{2,4}$/.test(s) ? s : '';
  }

  if (typeof v === 'object') {
    const cand = v.numero ?? v.codigo ?? v.cod ?? v.id ?? v.value ?? '';
    return pickLinhaNum(cand);
  }

  const s = String(v).trim();
  if (!s || isHex24(s) || isSentidoToken(s)) return '';

  // 1) sublinha explícita
  const sub = subToken(s);
  if (sub) return sub;

  // PRG_<TOKEN> (TOKEN pode vir com ".1" ou "_1")
  const up = s.toUpperCase();
  let m = /\bPRG[_-]([A-Z]?\d{2,4})(?:[._](\d))?\b/.exec(up);
  if (m) {
    const base = m[1], sufx = m[2];
    if (!(base.length === 4 && base.startsWith('20'))) {
      return sufx ? `${base}.${sufx}` : base;
    }
  }



  // 3) "A 446" / "A-446"
  m = /\b([A-Z])[-\s]?(\d{2,4})\b/i.exec(s);
  if (m) {
    const val = (m[1] + m[2]).toUpperCase();
    if (!(m[2].length === 4 && m[2].startsWith('20'))) return val;
  }

  // 4) número puro 2–4 dígitos (evita 20xx)
  m = /\b(\d{2,4})\b/.exec(s);
  if (m && !(m[1].length === 4 && m[1].startsWith('20'))) return m[1];

  // 5) "A307"
  m = /\b([A-Z]\d{2,4})\b/i.exec(s);
  if (m) return m[1].toUpperCase();

  return '';
}

// const LINE_KEYS = [
//   // clássicos
//   'linha','numero_linha','num_linha','linha_numero','linha_num',
//   'codlinha','codigo_linha','codigoLinha','cod_linha','linhaCodigo','linha_cod',
//   'routeShortName','route_short_name','shortname','short_name',
//
//   // variações vistas no Sonda/Sinóptico
//   'descTrajeto','dsTrajeto','trajetoDescricao','nmTrajeto','nomeTrajeto',
//   'dsLinha','nmLinha','linhaDescricao','linhaDesc','linhaNome'
// ];

// varredura profunda procurando números de linha (não utilizada atualmente)
// function deepFindLinhaNum(obj) {
//   const seen = new Set();
//   const stack = [obj];
//   while (stack.length) {
//     const cur = stack.pop();
//     if (!cur || typeof cur !== 'object' || seen.has(cur)) continue;
//     seen.add(cur);
//
//     for (const [k, v] of Object.entries(cur)) {
//       // tenta pegar número em chaves "boas"
//       if (LINE_KEYS.some(key => k.toLowerCase().includes(key.toLowerCase()))) {
//         const n = pickLinhaNum(v);
//         if (n) return n;
//       }
//       if (v && typeof v === 'object') stack.push(v);
//     }
//   }
//   return '';
// }

function extrairNumeroLinha(reg = {}) {
  // 0) fontes mais confiáveis
      const prefer = [reg?.descTrajeto, reg?.dsTrajeto, reg?.nmTrajeto, reg?.nomeTrajeto, reg?.trajeto, reg?.trajetoNome];
  for (const cand of prefer) {
    if (!cand) continue;
    const sub = subToken(cand);
    if (sub) return sub;
    const m = /\bPRG[_-]([A-Z]?\d{2,4}(?:\.\d)?)\b/.exec(String(cand).toUpperCase());
    if (m) return m[1];
  }

  // 1) campos diretos
  const diretos = [
    reg.numeroLinha, reg.linhaNumero, reg.linhaNum, reg.codigoLinha,
    reg.codLinha, reg.codigo, reg.linha, reg.routeShortName, reg.linhaId,
  ];
  for (const c of diretos) {
    const n = pickLinhaNum(c);
    if (n) return n;
  }

  // 2) aninhados comuns
  const aninhados = [
    reg?.trajeto?.linha, reg?.trajeto?.linhaNumero,
    reg?.trajeto?.linha?.numero, reg?.trajeto?.linha?.codigo,
    typeof reg?.linha === 'object' ? reg?.linha?.numero : null,
    typeof reg?.linha === 'object' ? reg?.linha?.codigo : null,
  ];
  for (const c of aninhados) {
    const n = pickLinhaNum(c);
    if (n) return n;
  }

  // 3) textual
  const textos = [
    reg.descTrajeto, reg.dsTrajeto, reg.trajetoNome, reg.trajeto,
    reg.descricao, reg.observacao, reg.observações, reg.motivo,
  ].filter(Boolean).join(' ');

  if (textos) {
    const up = textos.toUpperCase()
      .replace(/\b(IDA|VOLTA|VO|ID)\b/g, ' ')
      .replace(/\b(?:RESPOSTA|RESP\.)\s*/g, 'A ');

    const sub = subToken(up);
    if (sub) return sub;

    let m = /\bPRG[_-]([A-Z]?\d{2,4})(?:[._](\d))?\b/.exec(up);
    if (m) return m[2] ? `${m[1]}.${m[2]}` : m[1];

    m = /\b([A-Z])[-\s]?(\d{2,4})\b/.exec(up);
    if (m && !(m[2].length === 4 && m[2].startsWith('20'))) return (m[1] + m[2]).toUpperCase();

    m = /\b([A-Z]\d{2,4})\b/.exec(up);
    if (m) return m[1];

    m = /\b(\d{2,4})\b/.exec(up);
    if (m && !(m[1].length === 4 && m[1].startsWith('20'))) return m[1];
  }

  // 4) varredura profunda (procura qualquer PRG_xxx(.n) ou sublinha solta)
  const mDeepPrg = deepFindFirstMatch(reg, /\bPRG[_-]([A-Z]?\d{2,4})(?:[._](\d))?\b/);
  if (mDeepPrg) return mDeepPrg[2] ? `${mDeepPrg[1]}.${mDeepPrg[2]}` : mDeepPrg[1];

  const mDeepSub = deepFindFirstMatch(reg, /\b([A-Z]?\d{2,4})[._](\d)\b/);
  if (mDeepSub) {
    const base = mDeepSub[1];
    if (!(base.length === 4 && base.startsWith('20'))) return `${base}.${mDeepSub[2]}`;
  }

  // 4.5) último tiro: varrer o JSON inteiro como string
  try {
    const blob = JSON.stringify(reg, null, 0).toUpperCase();
    let m = blob.match(/\bPRG[_-]([A-Z]?\d{2,4})(?:[._](\d))?\b/);
    if (m) return m[2] ? `${m[1]}.${m[2]}` : m[1];
    m = blob.match(/\b([A-Z]?\d{2,4})[._](\d)\b/);
    if (m && !(m[1].length === 4 && m[1].startsWith('20'))) return `${m[1]}.${m[2]}`;
  } catch {}

  // 5) resíduos
  const raw = String(reg?.linha || '').trim();
  if (isHex24(raw)) return `ID:${raw.slice(0, 6)}…`;
  if (isSentidoToken(raw)) return '';
  return '';
}

function derivarSentido(reg = {}) {
  const blob = JSON.stringify(reg || {}).toUpperCase();
  const hasIda = /\bIDA\b/.test(blob);
  const hasVolta = /\bVOLTA\b/.test(blob);
  if (hasIda && hasVolta) return 'IDA';
  if (hasVolta) return 'VOLTA';
  if (hasIda) return 'IDA';
  return '';
}

function formatarLinha(reg) {
  const raw = reg?.linha;

  // "Resposta 307" / "Resp. 626" → "A307"
  if (typeof raw === 'string') {
    const m = raw.match(/^\s*(?:Resposta|Resp\.)\s*(\d{2,4})\s*$/i);
    if (m) return `A${m[1]}`.toUpperCase();
  }

  // tenta extrair número de todos os caminhos
  const num = extrairNumeroLinha(reg);
  if (num) return num;

  // se o backend enfiou um ObjectId aqui, mostre curto
  const s = String(raw || '').trim();
  if (isHex24(s)) {
    const id = (reg?.linhaId || s).toString();
    return `ID:${id.slice(0, 6)}…`;
  }

  // se veio "IDA/VOLTA" por engano, não mostre isso como Linha
  if (isSentidoToken(s)) return '—';

  // se vier "Identificação: ...", mantém seu comportamento amigável
  if (/^\s*Identificação:/i.test(s)) {
    const id = (reg?.linhaId || reg?.trajetoId || s).toString();
    return `ID:${id.slice(0, 6)}…`;
  }

  // último recurso: alguma denominação de linha (NÃO usar trajeto)
  const alt = reg?.linhaNome || reg?.linhaDescricao || reg?.linhaDesc || '';
  return (alt && String(alt).trim()) || '—';
}

const pad2 = (n) => String(n).padStart(2, '0');
function defaultPeriodToday() {
  const d = dayjs();
  const hoje = d.format('YYYY-MM-DD');
  return { di: hoje, hi: '00:00', df: hoje, hf: '23:59' };
}

// ---------------- componente ----------------
export default function PenalizacaoPanel() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // período local e independente (00:00–23:59 do dia atual)
  const [{ di, hi, df, hf }, setLocalPeriod] = useState(defaultPeriodToday());
  useEffect(() => { setLocalPeriod(defaultPeriodToday()); }, []);

  // Período estável para o RANKING: sempre mês do DF (ou DI), 01 -> hoje (se mês atual) ou fim do mês
  const rankingRange = useMemo(() => {
    const anchor = dayjs(df || di);
    const d0 = anchor.startOf('month');
    const d1 = anchor.isSame(dayjs(), 'month') ? dayjs().endOf('day') : anchor.endOf('month');
    return {
      inicio: d0.format('YYYY-MM-DD HH:mm:ss'),
      fim:    d1.format('YYYY-MM-DD HH:mm:ss'),
      monthKey: anchor.format('YYYY-MM'),
    };
  }, [di, df]);

  // payload para o backend
  const payload = useMemo(() => {
    const start = dayjs(`${di} ${hi}`);
    const end   = dayjs(`${df} ${hf}`);
    return {
      inicio: start.format('YYYY-MM-DD HH:mm:ss'),
      fim:    end.format('YYYY-MM-DD HH:mm:ss'),
      linhasSelecionadas: [],
    };
  }, [di, hi, df, hf]);

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['penalizacoes', payload],
    queryFn: () => getPenalizacoesRelatorio(payload),
    staleTime: 30_000,
  });

  const itens = useMemo(() => (Array.isArray(data?.itens) ? data.itens : []), [data?.itens]);
  const resumo = useMemo(() => (data?.resumo ?? { totalReais: 0, quantidade: 0 }), [data?.resumo]);

  // linhas da tabela
  const rows = useMemo(() => itens.map((r, idx) => {
    //  Valor: tenta campos conhecidos, senão tenta extrair do começo do dsObservacao
    const motivoTxt = r?.motivo ?? r?.dsObservacao ?? '';
    const valorTexto = r?.valor ?? r?.penalizacoes ?? r?.penalizacao ?? r?.valorPenalizacao
                    ?? (typeof motivoTxt === 'string' ? (motivoTxt.match(/(\d+[.,]\d{2})/)?.[1] ?? 0) : 0);
    const valorNum = parseBRNumber(valorTexto);

    // Linha (normalizada) e subtítulo original
    const linhaOriginal  = (r?.linha ?? r?.descTrajeto ?? r?.dsTrajeto ?? '').toString();
    const linhaPrincipal = formatarLinha({ ...r, linha: r?.linha ?? r?.descTrajeto ?? r?.dsTrajeto });
    const normalizaComp = (txt = '') => txt.replace(/\b(?:RESPOSTA|RESP\.)\s*/gi, 'A').replace(/\s+/g, '').toUpperCase();
    const linhaNomeFinal = linhaOriginal && normalizaComp(linhaOriginal) !== normalizaComp(linhaPrincipal)
      ? linhaOriginal
      : '';

    // Data / hora / usuário vindos do backend novo
    const dataStr = r?.data ?? r?.dtObservacao ?? r?.dtObseravacao ?? ''; // (o dump veio com dtObseravacao)
    const horaStr = r?.hora ?? r?.horaRealizada ?? r?.horaPartida ?? '';
    const usuario = r?.usuario ?? r?.nmUsuario ?? '';

    return {
      id: `${r.linhaId ?? r.trajetoId ?? r.idObservacao ?? 'x'}-${idx}`,
      linha: linhaPrincipal,
      linhaNome: linhaNomeFinal,
      motivo: motivoTxt || '',
      valor: valorNum,
      data: dataStr,
      hora: horaStr,
      usuario,
      trajeto: derivarSentido({ ...r, trajeto: r?.trajeto, trajetoNome: r?.trajetoNome, descTrajeto: r?.descTrajeto }),
    };
  }), [itens]);

  // exportação CSV
  const handleExportCSV = () => {
    if (!rows.length) return;
    const header = ['Linha','Motivo','Penalizações','Data','Hora','Usuário','Trajeto'];
    const csv = [
      header.join(';'),
      ...rows.map(r => [
        `"${r.linha === '—' ? '' : r.linha}"`,
        `"${(r.motivo || '').replace(/"/g, '""')}"`,
        penalFmt(r.valor).replace('.', ','),
        r.data, r.hora, `"${(r.usuario||'').replace(/"/g,'""')}"`, r.trajeto,
      ].join(';'))
    ].join('\r\n');

    // <- BOM aqui para Excel entender UTF-8 (corrige â€” e acentos)
    const bom = '\uFEFF';
    const blob = new Blob([bom, csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `penalizacoes_${di}_${pad2(dayjs().hour())}${pad2(dayjs().minute())}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // colunas
  const columns = [
    {
      field: 'linha',
      headerName: 'Linha',
      headerAlign: 'center',
      align: 'center',
      flex: 1,
      minWidth: 140,
      renderCell: ({ row }) => {
        const principal = String(row.linha || '');
        const subtitulo = row.linhaNome || '';
        return (
          <Box sx={{ width: '100%' }}>
            <Stack spacing={0.2} sx={{ alignItems: 'center' }}>
              <Typography variant="subtitle2">{principal}</Typography>
              {subtitulo && (
                <Typography variant="caption" color="text.secondary" noWrap>
                  {subtitulo}
                </Typography>
              )}
            </Stack>
          </Box>
        );
      },
    },

    {
      field: 'motivo',
      headerName: 'Motivo',
      headerAlign: 'center',
      align: 'center',
      flex: 2,
      minWidth: 300,
    },
    {
      field: 'valor',
      headerName: 'Penalizações',
      headerAlign: 'center',
      align: 'center',
      width: 150,
      renderCell: ({ row }) => {
        const v = Number(row.valor || 0);
        const color = v >= 1 ? 'error.main' : v >= 0.5 ? 'warning.main' : 'text.primary';
        return (
          <Typography variant="subtitle2" sx={{ color, fontWeight: 700 }}>
            {penalFmt(v)}
          </Typography>
        );
      },
    },
    {
      field: 'data',
      headerName: 'Date',
      headerAlign: 'center',
      align: 'center',
      width: 120,
      renderCell: (p) => (
        <Typography variant="body2">
          {p.value}
        </Typography>
      ),
    },
    {
      field: 'hora',
      headerName: 'Hora',
      headerAlign: 'center',
      align: 'center',
      width: 110,
      renderCell: (p) => (
        <Typography variant="body2">
          {p.value}
        </Typography>
      ),
    },
    {
      field: 'usuario',
      headerName: 'Usuário',
      headerAlign: 'center',
      align: 'center',
      width: 200,
      renderCell: ({ value }) => (
        <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="body2" noWrap>{value}</Typography>
        </Box>
      ),
    },
    {
      field: 'trajeto',
      headerName: 'Trajeto',
      headerAlign: 'center',
      align: 'center',
      width: 110,
      renderCell: ({ value }) => {
        const sense = String(value || '').toUpperCase();
        const chipColor =
          sense === 'IDA'  ? 'info' :
          sense === 'VOLTA'? 'secondary' : 'default';
        return <Chip label={sense || '-'} size="small" color={chipColor} />;
      }
    },
  ];

  // estilos da grade
  const gridSx = {
    /* Cabeçalho fixo + borda */
    '& .MuiDataGrid-columnHeaders': {
      position: 'sticky',
      top: 0,
      zIndex: 1,
      background: (t) => t.palette.background.paper,
      borderBottom: (t) => `1px solid ${t.palette.divider}`,
    },

    /* Título do cabeçalho em negrito */
    '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 700 },

    /* Centraliza header e células no eixo X e Y */
    '& .MuiDataGrid-columnHeader, & .MuiDataGrid-cell': {
      display: 'flex',
      alignItems: 'center',      // ← centro vertical
      justifyContent: 'center',  // ← centro horizontal
    },

    /* Em algumas versões o conteúdo interno fica em .MuiDataGrid-cellContent */
    '& .MuiDataGrid-cell .MuiDataGrid-cellContent': {
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
    },

    /* Zebra */
    '& .MuiDataGrid-row:nth-of-type(odd)': {
      backgroundColor: (t) => t.palette.action.hover,
    },

    /* Borda e fonte */
    '& .MuiDataGrid-cell': {
      borderBottom: (t) => `1px solid ${t.palette.divider}`,
      fontFamily: (t) => t.typography.fontFamily,
    },
  };


  // cores dinâmicas dos cards (claro x escuro)
  const cards = [
    {
      label: 'Viagens penalizadas',
      valor: resumo?.quantidade ?? 0,
      icon: IconNaoRealizada,
      lightBg: '#e3f2fd',
      leftLight: '#1976d2',
      leftDark: theme.palette.primary.main,
    },
    {
      label: 'Penalizações',
      valor: new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        .format(Number(resumo?.totalReais ?? 0)),
      icon: MultaIcon,
      lightBg: '#e8f5e9',
      leftLight: '#E80070',
      leftDark: theme.palette.success.main,
    },
  ];

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'repeat(12, 1fr)' },
        gap: 2,
        alignItems: 'stretch',
        mb: 2,
      }}
    >
      {/* Cards – agora mais estreitos (2 colunas cada) */}
      {cards.map((c, i) => {
        const bg = isDark ? theme.palette.background.paper : c.lightBg;
        const left = isDark ? c.leftDark : c.leftLight;
        const titleColor = isDark ? theme.palette.common.white : 'inherit';
        const subtitleColor = isDark ? 'rgba(255,255,255,0.75)' : theme.palette.text.secondary;

        return (
          <Paper
            key={i}
            elevation={3}
            sx={{
              gridColumn: { xs: '1 / -1', md: 'span 2' }, // ⬅️ antes era span 3
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              p: 2,
              minHeight: 78,
              backgroundColor: bg,
              borderLeft: `6px solid ${left}`,
              borderRadius: 2,
            }}
          >
            <Box component="img" src={c.icon} alt={c.label} sx={{ width: 36, height: 36, objectFit: 'contain' }} />
            <Box sx={{ lineHeight: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, textAlign: 'left', color: titleColor }}>
                {c.valor}
              </Typography>
              <Typography variant="body2" sx={{ textAlign: 'left', color: subtitleColor }}>
                {c.label}
              </Typography>
            </Box>
          </Paper>
        );
      })}

      {/* Período + ações – agora mais largo (8 colunas) */}
      <Paper
        elevation={3}
        sx={(t) => ({
          gridColumn: { xs: '1 / -1', md: 'span 8' }, // ⬅️ antes era span 6
          p: 1.25,
          borderRadius: 3,
          boxShadow: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          flexWrap: 'wrap',
          bgcolor: t.palette.mode === 'dark'
            ? alpha(t.palette.background.paper, 0.6)
            : t.palette.background.paper,
          border: '1px solid',
          borderColor: 'divider',
        })}
      >
        <Chip
          icon={<FilterAltRoundedIcon />}
          label="Período"
          variant="outlined"
          sx={{ fontWeight: 600 }}
        />

        <TextField
          label="Data Início"
          type="date"
          size="small"
          value={di}
          onChange={(e) => setLocalPeriod(p => ({ ...p, di: e.target.value }))}
          InputLabelProps={{ shrink: true }}
          sx={{ flex: '1 1 180px', minWidth: 160 }}
        />
        <TextField
          label="Hora Início"
          type="time"
          size="small"
          value={hi}
          onChange={(e) => setLocalPeriod(p => ({ ...p, hi: e.target.value }))}
          InputLabelProps={{ shrink: true }}
          sx={{ width: { xs: '100%', sm: 140 } }}
        />
        <TextField
          label="Data Fim"
          type="date"
          size="small"
          value={df}
          onChange={(e) => setLocalPeriod(p => ({ ...p, df: e.target.value }))}
          InputLabelProps={{ shrink: true }}
          sx={{ flex: '1 1 180px', minWidth: 160 }}
        />
        <TextField
          label="Hora Fim"
          type="time"
          size="small"
          value={hf}
          onChange={(e) => setLocalPeriod(p => ({ ...p, hf: e.target.value }))}
          InputLabelProps={{ shrink: true }}
          sx={{ width: { xs: '100%', sm: 140 } }}
        />

        <Divider flexItem orientation="vertical" sx={{ mx: 0.5 }} />

        <Tooltip title="Atualizar">
          <span>
            <IconButton
              onClick={() => refetch()}
              disabled={isFetching}
              sx={(t) => ({
                width: 36,
                height: 36,
                bgcolor: alpha(t.palette.primary.main, 0.12),
                color: t.palette.primary.main,
                '&:hover': { bgcolor: alpha(t.palette.primary.main, 0.2) },
              })}
            >
              <RefreshRoundedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Exportar CSV">
          <IconButton
            onClick={handleExportCSV}
            sx={(t) => ({
              width: 36,
              height: 36,
              bgcolor: alpha(t.palette.secondary.main, 0.12),
              color: t.palette.secondary.main,
              '&:hover': { bgcolor: alpha(t.palette.secondary.main, 0.2) },
            })}
          >
            <DownloadRoundedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Paper>

      {/* Tabela (linha inteira) */}
      <Box sx={{ gridColumn: '1 / -1', width: '100%', height: 520 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={isLoading}
          disableRowSelectionOnClick
          pageSizeOptions={[10, 25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          sx={gridSx}
          rowHeight={52}
        />
      </Box>
      {/* Ranking por Empresa */}
      <Box sx={{ gridColumn: '1 / -1', width: '100%', mt: 2 }}>
        <RankingPenalizacoesPorEmpresa
          key={rankingRange.monthKey}      // remonta ao trocar de mês (evita resíduos)
          inicio={rankingRange.inicio}     // mês ancorado no DF
          fim={rankingRange.fim}
          empresas={EMPRESAS_FIXAS}        // força o conjunto completo de empresas
          height={520}
        />
      </Box>
      
    </Box>
  );
}
